import type { RefObject } from "react"
import type { UseFormReturn } from "react-hook-form"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { Input } from "@/modules/shared/ui/components/input"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/modules/shared/ui/components/select"
import { Button } from "@/modules/shared/ui/components/button"
import { Currency } from "@/modules/organizations/enums/currency"
import type { CompanyFormValues } from "./registerOrganizationSchema"

interface RegisterOrganizationDetailsStepProps {
  form: UseFormReturn<CompanyFormValues>
  addressSearchRef: RefObject<HTMLInputElement | null>
  onNext: () => void
  onBack: () => void
}

export function RegisterOrganizationDetailsStep({
  form,
  addressSearchRef,
  onNext,
  onBack,
}: RegisterOrganizationDetailsStepProps) {
  return (
    <>
      <p className="text-center mb-8 text-muted-foreground">
        Complete your company information
      </p>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onNext)}
          className="space-y-4 max-w-2xl mx-auto"
        >
          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Company Name<span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Enter your company name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="streetAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Street Address<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="123 Business Ave"
                      {...field}
                      ref={addressSearchRef}
                    />
                  </FormControl>
                  <FormMessage className="min-h-5" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    City<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Business City" {...field} />
                  </FormControl>
                  <FormMessage className="min-h-5" />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Province<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Province" {...field} />
                  </FormControl>
                  <FormMessage className="min-h-5" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="zipCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Postal Code<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="12345" {...field} />
                  </FormControl>
                  <FormMessage className="min-h-5" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Country<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Country" {...field} />
                  </FormControl>
                  <FormMessage className="min-h-5" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Description (Optional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Brief description of your company"
                    rows={3}
                    className="resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Preferred Currency</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={Currency.ZAR}>
                      {Currency.ZAR} (South African Rand)
                    </SelectItem>
                    <SelectItem value={Currency.USD}>
                      {Currency.USD} (US Dollar)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end space-x-4 pt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={onBack}
              className="px-6 py-2 text-muted-foreground hover:text-foreground hover:bg-transparent"
            >
              Back
            </Button>
            <Button
              type="submit"
              className="px-6 py-2 bg-primary text-primary-foreground rounded-md font-semibold"
            >
              Next: Choose Plan
            </Button>
          </div>
        </form>
      </Form>
    </>
  )
}
